import { getApi } from "./axiosExtensions";
import { General } from "../constants";
import { isEmpty, isFull } from "../utils";
import { doRefreshToken } from "../../store/reducers/accountService";
import { customerSignoutCompleted, routeChangeBegan } from "../../store/middleware/actionUtils";
import { store } from "../../store/store";
import { Copy } from "../copy";

const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

export const apiCall = async ({
  data,
  baseUrl,
  url,
  method,
  accessToken,
  refreshToken,
  headers = {},
  type = "json",
  retry = false,
  retryCount = 3,
  retryDelay = 1000,
}) => {
  const onTokenRefresh = ({ accessToken, refreshToken }) => {
    store.dispatch(
      doRefreshToken({
        accessToken,
        refreshToken,
      })
    );
  };

  const onSignInExpired = () => {
    store.dispatch(customerSignoutCompleted());
    store.dispatch(routeChangeBegan({ intent: "logout" }));
  };

  switch (type) {
    case "json":
      headers["Content-Type"] = "application/json;charset=UTF-8";
      break;

    case "text":
      headers["Content-Type"] = "text/plain;charset=utf-8";
      break;

    case "file":
    case "multipart":
      headers["Content-Type"] = "multipart/form-data;charset=UTF-8";
      break;
    default:
      break;
  }

  const executeRequest = async (attemptNumber = 1) => {
    try {
      if (isEmpty(accessToken)) {
        console.warn("⚠️ access token was not filled from calling component.");
      }

      const response = await getApi({
        baseUrl,
        onTokenRefresh,
        onSignInExpired,
      }).request({
        url,
        headers,
        method,
        data,
        responseType: type,
        meta: {
          accessToken,
          refreshToken,
        },
      });

      if (!response.status) {
        return {
          message: Copy.Common.ErrorMessage,
          success: false,
          code: General.ErrorCodes.GeneralFailure,
        };
      }
      return {
        response,
        success: true,
      };
    } catch (error) {
      // Check if we should retry
      const isRetryableError = 
        error.response?.status >= 500 || // Server errors
        error.response?.status === 408 || // Request timeout
        error.code === 'ECONNABORTED' || // Timeout
        error.code === 'ERR_NETWORK' || // Network error
        !error.response; // No response (network issue)

      if (retry && isRetryableError && attemptNumber < retryCount) {
        console.log(`🔄 Retrying API call (attempt ${attemptNumber + 1}/${retryCount}): ${url}`);
        await delay(retryDelay * attemptNumber); // Exponential backoff
        return executeRequest(attemptNumber + 1);
      }

      let userFriendlyMessage = error.response?.data?.description || Copy.Common.AlternativeErrorMessage;
      let userFriendlyCode =
        error.response?.data?.code || General.ErrorCodes.GeneralFailure;

      if (error.response?.status === General.StatusCodes.Forbidden) {
        userFriendlyMessage = Copy.Common.ForbiddenErrorMessage;
        userFriendlyCode = General.ErrorCodes.AccessDenied;
      }

      // A failure on the server's side is one the user cannot put right, so its message ends with
      // the call's correlation id: the reference the request is found by in the services' logs.
      const correlationId = error.config?.headers?.[General.Headers.CorrelationId];

      if (error.response?.status >= General.StatusCodes.InternalServerError && isFull(correlationId)) {
        userFriendlyMessage = Copy.Common.ErrorMessageWithReference(userFriendlyMessage, correlationId);
      }

      let messagePayload = {
        statusCode: error.response?.status || "",
        message: error.message || messagePayload.userFriendlyMessage,
        code: userFriendlyCode,
        success: false,
        userFriendlyMessage,
      };

      return messagePayload;
    }
  };

  return executeRequest();
};
